/**
 * Draws one design of CoverDesigns on the cover of the book : layout() lays the photo out (masked
 * and framed the way the design wants it), paints the whole cover in its colour and adds its
 * stickers and its back cover photo ; writeTitle() writes the title and the subtitle, each one on one
 * line of its block.
 *
 * The creator fills the photo slots afterwards (ChronologicalCreator._fillCover) and lends its text
 * fitting (_fitOneLine), its subtitle (_subtitleFor), its plain back cover and its media loading.
 */
fx.ns("covers", class DesignedCover {

    constructor(creator, design)
    {
        this.creator = creator;
        this.design = design;
    }

    get book()
    {
        return this.creator.book;
    }

    get data()
    {
        return this.creator.data;
    }

    /** The height a design title takes, stage margin included, per unit of its font size. */
    get TITLE_LINE_HEIGHT()
    {
        return 1.45;
    }

    /** The margin the stage Text component keeps inside its block, on each side (Text MARGINS / 2). */
    get TEXT_PADDING()
    {
        return 5;
    }

    get LOSTBORDER()
    {
        return 5;
    }

    // ------------------------------- layout -------------------------------- //

    /**
     * Lays the cover out : the frame of the design under the curated photo, the whole cover painted
     * in the colour of the design, back and slice included, then the back photo and the stickers.
     * Answers a Promise, the stickers being loaded from the server first.
     */
    layout()
    {
        let design = this.design, book = this.book;
        let width = book.pageWidth, height = book.pageHeight, slice = book.sliceWidth || 0;
        let rect = this._photoRect(design.photo);
        let components = [];

        // the frame goes in first : the stage draws the components in the order they are added
        if (design.frame)
            components.push(new models.ThemeLayoutComponent({
                component_type : "shape",
                content : 1,                     // ShapesRepository : the plain rectangle
                bgcolor : design.frame.color,
                side : design.frame.color,
                borderid : 0,
                x : design.frame.x * width,
                y : design.frame.y * height,
                width : design.frame.width * width,
                height : design.frame.height * height
            }));

        components.push(this._photoComponent(rect, design.photo.mask));

        book.cover.applyLayout(new models.ThemeLayout({ components : components }), width, height, slice, "right");

        let background = book.cover.getBackground ? book.cover.getBackground("right") : null;
        if (background)
            background.update({ content : "", bgcolor : design.background, align : "" });

        this.creator._plainBackCover(design.background);

        // added once the back is cleared, which removes everything but its background
        if (design.backPhoto)
            book.cover.addComponent(this._photoComponent(this._photoRect(design.backPhoto)).JSON(), "left", width, height, slice, true);

        return this._addStickers();
    }

    _photoComponent(rect, mask)
    {
        return new models.ThemeLayoutComponent({
            component_type : "photo",
            photomask : this._maskId(mask),
            x : rect.x,
            y : rect.y,
            width : rect.width,
            height : rect.height,
            width_inner : 0,
            height_inner : 0
        });
    }

    /**
     * The stickers of the design, over the photo : their medias loaded first (the stage would load
     * them too, but only once drawn, and the creation goes on meanwhile), never holding the creation
     * (ChronologicalCreator._loadLayoutMedias gives up after 10 s).
     */
    _addStickers()
    {
        let stickers = this.design.stickers || [];

        if (!stickers.length)
            return Promise.resolve();

        let book = this.book, width = book.pageWidth, height = book.pageHeight, slice = book.sliceWidth || 0;
        let components = stickers.map((sticker) => new models.ThemeLayoutComponent(Object.assign({ component_type : "sticker", content : sticker.id, rotation : 0 }, this._stickerRect(sticker))));

        return this.creator._loadLayoutMedias(new models.ThemeLayout({ components : components })).then(() => {
            for (let i = 0; i < components.length; i++)
            {
                let json = components[i].JSON();
                json.x = width + slice + Number(json.x);

                book.cover.addComponent(json, "right", width, height, slice, true);
            }
        });
    }

    /**
     * The place of a sticker on the front page : sized on the page width, anchored to its corner or
     * to the bottom of the photo, and never past the bottom of the page.
     */
    _stickerRect(sticker)
    {
        let width = this.book.pageWidth, height = this.book.pageHeight;
        let w = sticker.width * width, h = sticker.height * width;
        let y = sticker.y * height;

        if (sticker.belowPhoto !== undefined)
        {
            let photo = this._photoRect(this.design.photo);
            y = Math.min(photo.y + photo.height + sticker.belowPhoto * width, height - this.LOSTBORDER) - h;
        }
        else if (sticker.bottom !== undefined)
            y = height - sticker.bottom * height - h;

        return {
            x : (sticker.right !== undefined) ? width - sticker.right * width - w : sticker.x * width,
            y : y,
            width : w,
            height : h
        };
    }

    /**
     * The photo_masks id of a design mask : an id as it is, a file name ("arche.png") looked up in
     * the masks of the editor, its id differing from one database to another. "" (no mask) when
     * that mask is not there.
     */
    _maskId(mask)
    {
        if (!mask || typeof mask != "string")
            return mask || "";

        let found = (this.data.masks && this.data.masks.find) ? this.data.masks.find({ file : mask }) : null;

        return found ? found.id : "";
    }

    /**
     * The place a design gives a photo ; a `square` photo is as tall as it is wide (the masks). A
     * `ratio` (height / width) keeps the photo at those proportions whatever the book : as tall as
     * its place, as wide as that makes it within `maxWidth` of the page (its own width by default),
     * centered across : a portrait book gets a wider photo, a landscape one a narrower, and the
     * mask drawn for those proportions (the arch) is never stretched.
     */
    _photoRect(photo)
    {
        let width = this.book.pageWidth, height = this.book.pageHeight;
        let photoWidth = photo.width * width;

        if (photo.ratio)
        {
            let boxHeight = photo.height * height;
            let fitted = Math.min((photo.maxWidth || photo.width) * width, boxHeight / photo.ratio);

            return {
                x : (width - fitted) / 2,
                y : photo.y * height,
                width : fitted,
                height : fitted * photo.ratio
            };
        }

        return {
            x : photo.x * width,
            y : photo.y * height,
            width : photoWidth,
            height : photo.square ? photoWidth : photo.height * height
        };
    }

    // ------------------------------- texts --------------------------------- //

    /**
     * Writes the title and the subtitle, and the fixed heading of the design when it has one, each
     * one on one line : the block is the one the design gives (the printable width by default), the
     * stage margin added around it so that the text lands exactly there, and the font size reduced
     * until the line holds. Without a title only a design with a heading writes anything.
     */
    writeTitle(title)
    {
        let design = this.design;

        if (!title && !design.heading)
            return Promise.resolve();

        let lines = [{ spec : design.title, text : this._titleText(design.title, title), type : "title" }];

        if (design.heading)
            lines.unshift({ spec : design.heading, text : this._titleText(design.heading, design.heading.text), type : "text" });

        let subtitle = this._subtitle(title);
        if (design.subtitle && subtitle)
            lines.push({ spec : design.subtitle, text : subtitle, type : "text" });

        lines = lines.filter((line) => !!line.text);

        return this._loadFonts(lines.map((line) => line.spec))
            .then(() => Promise.all(lines.map((line) => {
                let rect = this._textRect(line.spec);

                // a subtitle under the title never climbs into it : the design places it as a
                // fraction of the page height, and a page wider than tall brings it up under a title
                // whose size is in millimetres
                if (line.spec == design.subtitle && !line.spec.fixed && line.spec.y > design.title.y)
                    rect.y = Math.max(rect.y, this._textRect(design.title).y + design.title.size * this.TITLE_LINE_HEIGHT);

                return this.creator._fitOneLine(this._textHtml(line.text, line.spec), rect.width)
                    .then((html) => ({ html : html, rect : rect, line : line }));
            })))
            .then((fitted) => {
                for (let i = 0; i < fitted.length; i++)
                    this._addText(fitted[i].html, fitted[i].line, fitted[i].rect);
            });
    }

    _titleText(spec, text)
    {
        return spec.upper ? String(text || "").toUpperCase() : text;
    }

    /** The line under the title : the subtitle of the category, opened by the prefix of the design. */
    _subtitle(title)
    {
        let spec = this.design.subtitle;
        let subtitle = this.creator._subtitleFor(title);

        if (spec && spec.prefix)
            subtitle = subtitle ? (spec.prefix + " • " + subtitle) : spec.prefix;

        if (spec && spec.upper)
            subtitle = String(subtitle || "").toUpperCase();

        // letters spread by thin spaces, a word space kept wider than the gap between letters
        if (spec && spec.spaced && subtitle)
            subtitle = Array.from(String(subtitle)).join(" ");

        return subtitle;
    }

    /**
     * The text component of a design line : the block of the design, widened by the margin the
     * stage keeps inside it, so that the text itself falls where the design places it.
     */
    _textRect(spec)
    {
        let width = this.book.pageWidth, height = this.book.pageHeight, slice = this.book.sliceWidth || 0;
        let x = (spec.x !== undefined) ? spec.x : 0.08;
        let blockWidth = (spec.width !== undefined) ? spec.width : 1 - 2 * x;
        let padding = this.TEXT_PADDING;

        return {
            x : width + slice + x * width - padding,
            y : spec.y * height - padding,
            width : blockWidth * width + 2 * padding - this.LOSTBORDER
        };
    }

    _textHtml(text, spec)
    {
        let safe = String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

        // only Lora and Open Sans carry a real bold and a real italic (font.styles), the designs
        // asking for one use those two
        if (spec.italic)
            safe = "<em>" + safe + "</em>";

        if (spec.bold)
            safe = "<strong>" + safe + "</strong>";

        return "<p style=\"text-align: " + (spec.align || "center") + ";\">"
             + "<span style=\"font-family: " + spec.font + "; font-size: " + spec.size + "px; color: " + spec.color + ";\">"
             + safe + "</span></p>";
    }

    _addText(html, line, rect)
    {
        let spec = line.spec;
        let font = (this.data.fonts && this.data.fonts.find) ? this.data.fonts.find({ name : spec.font }) : null;

        this.book.cover.addComponent({
            component_type : "text",
            content_type : line.type,
            content : html,
            text_color : spec.color,
            font_family : font ? font.id : 1,
            font_size : spec.size,
            align : spec.align || "center",
            x : rect.x,
            y : rect.y,
            width : rect.width,
            height : spec.size * 3,
            height_inner : -1   // the text component resizes itself to its content
        }, "right", this.book.pageWidth, this.book.pageHeight, this.book.sliceWidth || 0, true);   // as a layout text : placed as given, its colour kept
    }

    /**
     * The faces a design writes with, loaded before the lines are measured : a fallback font would
     * give _fitOneLine the wrong width. Bounded, a missing font simply falls back.
     */
    _loadFonts(specs)
    {
        if (typeof document == "undefined" || !document.fonts || !document.fonts.load)
            return Promise.resolve();

        let faces = specs.map((spec) => (spec.italic ? "italic " : "") + (spec.bold ? "bold " : "") + "12px \"" + spec.font + "\"");

        return Promise.race([
            Promise.all(faces.map((face) => {
                try { return document.fonts.load(face).catch(() => null); }
                catch (e) { return Promise.resolve(); }
            })),
            new Promise((resolve) => setTimeout(resolve, 3000))
        ]);
    }
});
